import { useState } from 'react'
import { WppAccordion, WppLabel, WppTypography } from '@wppopen/components-library-react'
import { Link } from 'react-router-dom'
import { getFormattedDate } from 'utils'
import { getUpdateTypeString } from './utils'
import { unknown } from 'utils/constants'
import type { BrandsDetailData } from '@contracts'
import styles from './VersionHistoryList.module.scss'

export type UpdateType = 'create' | 'edit' | 'archive' | 'approve'
export type VersionHistoryInfo = BrandsDetailData['version_history']
export type VersionUpdateInfo = BrandsDetailData['update_info']

interface VersionHistoryListProps<T> {
  currentVersion: T | null
  historyArray: VersionHistoryInfo
  onVersionClickCB: () => void
}

function VersionHistoryList<
  T extends {
    readonly id?: string
    readonly version?: number
    readonly update_info?: VersionUpdateInfo
    readonly created_at?: string
  },
>({ historyArray, currentVersion, onVersionClickCB }: VersionHistoryListProps<T>) {
  const [isExpanded, setIsExpanded] = useState<boolean>(false)

  if (currentVersion === null) return null

  const { update_info, version } = currentVersion

  const handleVersionChange = () => {
    setIsExpanded(false)
    onVersionClickCB()
  }

  return (
    <WppAccordion
      withDivider={false}
      size="s"
      expanded={isExpanded}
      className={styles.accordianContainer}
      data-testid="version-history-list"
    >
      <WppLabel
        className={styles.color_blue}
        slot="header"
        htmlFor="name"
        typography="s-strong"
        optional={false}
        config={{
          icon: update_info?.comment ? 'wpp-icon-info' : undefined,
          text: `Version ${version} ${getUpdateTypeString(update_info)}`,
          description: update_info?.comment,
        }}
        data-testid="version-label"
      />
      <ul>
        {historyArray?.map(version => (
          <li key={version._id} data-testid="version-list-item">
            <WppTypography type="s-strong">
              <Link
                to={`?version=${version.version}`}
                data-testid="version-link"
                className={styles.color_blue}
                onClick={handleVersionChange}
              >
                Version {version.version}
                {version.version && version.version > 1 && getUpdateTypeString(version.update_info)}{' '}
              </Link>
              <WppTypography type="s-body" data-testid="version-created">
                created {getFormattedDate(version?.created_at)} by {version.update_info?.user_name || unknown}
              </WppTypography>
            </WppTypography>
          </li>
        ))}
      </ul>
    </WppAccordion>
  )
}

export default VersionHistoryList
